"use client";
import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Menu, X, ArrowRight } from "lucide-react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useMotionValueEvent,
} from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { navLinks } from "@/data/landing";
import { useAuth } from "@clerk/nextjs";

import { usePathname } from "next/navigation";

export const Navbar = () => {
  const ref = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const { scrollY } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const [visible, setVisible] = useState<boolean>(false);

  useMotionValueEvent(scrollY, "change", (latest) => {
    if (latest > 100) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  });

  return (
    <motion.div ref={ref} className="w-full fixed top-0 inset-x-0 z-[100] p-4">
      <DesktopNav visible={visible} navItems={navLinks} pathname={pathname} />
      <MobileNav visible={visible} navItems={navLinks} pathname={pathname} />
    </motion.div>
  );
};

const Logo = () => {
  return (
    <Link href="/" className="flex items-center" aria-label="Tavlo - Home">
      <Image
        src="/logo-light.png"
        alt="Tavlo"
        width={110}
        height={40}
        style={{
          filter: "brightness(5) saturate(0.5)",
        }}
      />
    </Link>
  );
};

interface NavbarProps {
  navItems: {
    label: string;
    href: string;
  }[];
  visible: boolean;
  pathname: string;
}

const DesktopNav = ({ navItems, visible, pathname }: NavbarProps) => {
  const [hovered, setHovered] = useState<number | null>(null);
  const { isSignedIn } = useAuth();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const scrollToSection = (href: string) => {
    if (href.startsWith("/#") && pathname === "/") {
      const id = href.split("#")[1];
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        return true;
      }
    }
    
    if (href.startsWith("#")) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        return true;
      }
    }
    return false;
  };

  return (
    <motion.div
      onMouseLeave={() => {
        setHovered(null);
      }}
      initial={{
        width: "1280px", // Initial modest size
        y: 0,
        x: "-50%",
        top: 0,
        borderRadius: "2rem",
        backgroundColor: "transparent",
        backdropFilter: "none",
        boxShadow: "none",
      }}
      animate={{
        width: visible ? "950px" : "1280px", // Shrinks to a smaller size when visible
        y: visible ? 16 : 0,
        x: "-50%",
        top: visible ? 16 : 0,
        backdropFilter: visible ? "blur(10px)" : "none",
        boxShadow: visible
          ? "0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), 0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset"
          : "none",
        backgroundColor: visible ? "var(--surface)" : "transparent",
        borderRadius: visible ? "1rem" : "2rem",
      }}
      transition={{
        type: "spring",
        stiffness: 150,
        damping: 30,
      }}
      className={cn(
        "hidden lg:flex flex-row items-center justify-between py-3 px-6 fixed z-[60] left-1/2",
      )}
    >
      <Logo />
      <motion.div className="lg:flex flex-row flex-1 hidden items-center justify-center space-x-2 lg:space-x-2 text-base text-text-secondary font-medium transition duration-200">
        {navItems.map((navItem, idx: number) => (
          <a
            key={`link=${idx}`}
            href={navItem.href}
            onClick={(e) => {
              if (scrollToSection(navItem.href)) {
                e.preventDefault();
              }
            }}
            onMouseEnter={() => setHovered(idx)}
            className="text-text-secondary hover:text-text-primary relative px-4 py-2 min-h-[48px] min-w-[48px] flex items-center justify-center transition-colors"
            aria-label={`Navigate to ${navItem.label} section`}
          >
            {hovered === idx && (
              <motion.div
                layoutId="hovered"
                className="w-full h-full absolute inset-0 bg-border-light rounded-full"
              />
            )}
            <span className="relative z-20">{navItem.label}</span>
          </a>
        ))}
      </motion.div>
      <div className="flex items-center gap-3">
        {mounted && isSignedIn ? (
          <Link href="/today">
            <Button className="hidden md:flex bg-gradient-to-r from-brand-1 to-brand-2 text-white hover:shadow-lg hover:shadow-brand-1/20 rounded-full gap-2">
              Go to App <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        ) : (
          <>
            <Link href="/sign-in">
              <Button
                variant="ghost"
                className="hidden md:block text-text-secondary hover:text-brand-1"
              >
                Sign In
              </Button>
            </Link>
            <Link href="/sign-in">
              <Button className="hidden md:flex bg-gradient-to-r from-brand-1 to-brand-2 text-white hover:shadow-lg hover:shadow-brand-1/20 rounded-full">
                Try for free
              </Button>
            </Link>
          </>
        )}
      </div>
    </motion.div>
  );
};

const MobileNav = ({ navItems, visible, pathname }: NavbarProps) => {
  const [open, setOpen] = useState(false);
  const { isSignedIn } = useAuth();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const scrollToSection = (href: string) => {
    if (href.startsWith("/#") && pathname === "/") {
      const id = href.split("#")[1];
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        return true;
      }
    }

    if (href.startsWith("#")) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        return true;
      }
    }
    return false;
  };

  const handleNavClick = (href: string) => {
    // Close menu first
    setOpen(false);

    // Delay scroll until after menu closes (animation takes ~300ms)
    setTimeout(() => {
      scrollToSection(href);
    }, 350);

    return true;
  };

  return (
    <>
      <motion.div
        initial={{
          width: "calc(100% - 2rem)",
          y: 0,
          x: "-50%",
          left: "50%",
          top: 0,
          borderRadius: "2rem",
          backgroundColor: "transparent",
          backdropFilter: "none",
          boxShadow: "none",
        }}
        animate={{
          width: visible && !open ? "calc(90% - 2rem)" : "calc(100% - 2rem)",
          y: visible ? 16 : 0,
          x: "-50%",
          left: "50%",
          top: visible ? 16 : 0,
          backdropFilter: visible || open ? "blur(10px)" : "none",
          boxShadow:
            visible || open
              ? "0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), 0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset"
              : "none",
          backgroundColor: visible || open ? "var(--surface)" : "transparent",
          borderRadius: open ? "1rem" : visible ? "1rem" : "2rem",
        }}
        transition={{
          type: "spring",
          stiffness: 150,
          damping: 30,
        }}
        className={cn(
          "flex relative flex-col lg:hidden justify-between items-center bg-transparent fixed z-[101]",
          "rounded-2xl",
        )}
      >
        <div className="flex flex-row justify-between items-center w-full px-4 py-2">
          <Logo />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="p-2 min-h-[48px] min-w-[48px] flex items-center justify-center"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
            >
              {open ? (
                <X className="text-text-primary w-6 h-6" />
              ) : (
                <Menu className="text-text-primary w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{
                opacity: 0,
                height: 0,
              }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="flex bg-surface-solid inset-x-0 z-50 flex-col items-start justify-start gap-4 w-full px-4 py-8 shadow-xl rounded-b-2xl overflow-hidden"
            >
              {navItems.map((navItem, idx: number) => (
                <a
                  key={`link=${idx}`}
                  href={navItem.href}
                  onClick={(e) => {
                    if (handleNavClick(navItem.href)) {
                      e.preventDefault();
                    }
                  }}
                  className="relative text-text-secondary hover:text-text-primary w-full text-left py-2 font-medium min-h-[48px] flex items-center transition-colors"
                  aria-label={`Navigate to ${navItem.label} section`}
                >
                  <motion.span className="block">{navItem.label}</motion.span>
                </a>
              ))}
              {mounted && isSignedIn ? (
                <Link
                  href="/today"
                  className="w-full"
                  onClick={() => setOpen(false)}
                >
                  <Button
                    variant="default"
                    className="block md:hidden w-full bg-gradient-to-r from-brand-1 to-brand-2 gap-2"
                  >
                    Go to App <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              ) : (
                <>
                  <Link
                    href="/sign-in"
                    className="w-full"
                    onClick={() => setOpen(false)}
                  >
                    <Button
                      variant="outline"
                      className="block md:hidden w-full"
                    >
                      Sign In
                    </Button>
                  </Link>
                  <Link
                    href="/sign-in"
                    className="w-full"
                    onClick={() => setOpen(false)}
                  >
                    <Button
                      variant="default"
                      className="block md:hidden w-full bg-gradient-to-r from-brand-1 to-brand-2"
                    >
                      Try for free
                    </Button>
                  </Link>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
};

export default Navbar;
